import { EditableSelect } from 'features/libra/components/EditableSelect'
import { StyledCard } from 'features/libra/stepComponents/niiReport/cards/cardsStyles'
import {
  createFieldSetter,
  createFieldSetterWithDefault,
} from 'features/libra/stepComponents/niiReport/createFieldSetter'
import { EditableNotes } from 'features/libra/stepComponents/niiReport/NiiFieldComponents'
import {
  DescriptionItem,
  OptionalDescriptions,
} from 'features/libra/stepComponents/niiReport/OptionalDescriptions'
import { damagePredictionConfidenceValues } from 'features/libra/stepComponents/niiScreening/niiScreeningApi'
import { useNiiScreeningData } from 'features/libra/stepComponents/niiScreening/NiiScreeningDataContext'
import {
  BooleanOrUnknownOrNoHistorySelect,
  BooleanOrUnknownSelect,
} from 'features/libra/stepComponents/niiScreening/NiiScreeningFieldComponents'
import React, { FC, useMemo } from 'react'

const confidenceOptions = [
  { value: 'high', label: 'High', color: 'green' },
  { value: 'med', label: 'Med', color: 'orange' },
  { value: 'low', label: 'Low', color: 'red' },
] as const satisfies ReadonlyArray<{
  value: (typeof damagePredictionConfidenceValues)[number]
  label: string
  color: string
}>

export const RawNiiSuitabilityCard: FC = () => {
  const { niiScreening, setNiiScreening, isLoading } = useNiiScreeningData()

  const [
    setIsIntrinsicallySuitable,
    setPreviousInspectionHistoryValidity,
    setAreThereSimilarServiceAssets,
    setConfidenceInDamagePrediction,
    setSuitabilityJustification,
  ] = useMemo(
    () => [
      createFieldSetter('isIntrinsicallySuitable', setNiiScreening),
      createFieldSetter('previousInspectionHistoryValidity', setNiiScreening),
      createFieldSetter('areThereSimilarServiceAssets', setNiiScreening),
      createFieldSetterWithDefault(
        'confidenceInDamagePrediction',
        setNiiScreening,
        'med'
      ),
      createFieldSetter('suitabilityJustification', setNiiScreening),
    ],
    [setNiiScreening]
  )

  const items = useMemo<ReadonlyArray<DescriptionItem>>(
    () => [
      [
        'Is the vessel intrinsically suitable?',
        <BooleanOrUnknownSelect
          key='isIntrinsicallySuitable'
          value={niiScreening?.isIntrinsicallySuitable}
          onChange={setIsIntrinsicallySuitable}
        />,
      ] as const,
      [
        'Is previous inspection history still valid?',
        <BooleanOrUnknownOrNoHistorySelect
          key='previousInspectionHistoryValidity'
          value={niiScreening?.previousInspectionHistoryValidity}
          onChange={setPreviousInspectionHistoryValidity}
        />,
      ] as const,
      [
        'Are there similar service assets?',
        <BooleanOrUnknownSelect
          key='areThereSimilarServiceAssets'
          value={niiScreening?.areThereSimilarServiceAssets}
          onChange={setAreThereSimilarServiceAssets}
        />,
      ] as const,
      [
        'Confidence in Damage Prediction',
        <EditableSelect
          key='confidenceInDamagePrediction'
          value={niiScreening?.confidenceInDamagePrediction}
          onChange={(v) => setConfidenceInDamagePrediction(v)}
          options={confidenceOptions}
        />,
      ] as const,
      [
        'Justification',
        <EditableNotes
          key='suitabilityJustification'
          value={niiScreening?.suitabilityJustification}
          onChange={setSuitabilityJustification}
        />,
      ] as const,
    ],
    [
      niiScreening?.isIntrinsicallySuitable,
      niiScreening?.previousInspectionHistoryValidity,
      niiScreening?.areThereSimilarServiceAssets,
      niiScreening?.confidenceInDamagePrediction,
      niiScreening?.suitabilityJustification,
      setIsIntrinsicallySuitable,
      setPreviousInspectionHistoryValidity,
      setAreThereSimilarServiceAssets,
      setConfidenceInDamagePrediction,
      setSuitabilityJustification,
    ]
  )

  return (
    <StyledCard title='Suitability'>
      <OptionalDescriptions items={items} isLoading={isLoading} />
    </StyledCard>
  )
}

export const NiiSuitabilityCard = React.memo(RawNiiSuitabilityCard)
